import React, { useEffect } from 'react'

export type ToastVariant = 'success' | 'error' | 'info' | 'warning'

export interface ToastProps {
  id: string
  message: string
  variant: ToastVariant
  duration?: number
  onDismiss: (id: string) => void
}

const variantStyles: Record<ToastVariant, string> = {
  success: 'bg-success-green text-white',
  error: 'bg-error-red text-white',
  info: 'bg-charcoal text-white',
  warning: 'bg-warning-amber text-white',
}

const variantIcons: Record<ToastVariant, string> = {
  success: '✓',
  error: '✕',
  info: 'ℹ',
  warning: '⚠',
}

export const Toast: React.FC<ToastProps> = ({
  id,
  message,
  variant,
  duration = 4000,
  onDismiss,
}) => {
  useEffect(() => {
    const timer = setTimeout(() => onDismiss(id), duration)
    return () => clearTimeout(timer)
  }, [id, duration, onDismiss])

  return (
    <div
      role="status"
      aria-live="polite"
      className={`
        flex items-center gap-3 px-4 py-3 rounded-sm shadow-elevation-3
        min-w-[280px] max-w-[380px] animate-slide-up
        ${variantStyles[variant]}
      `}
    >
      <span className="font-bold text-sm shrink-0" aria-hidden="true">
        {variantIcons[variant]}
      </span>
      <p className="text-body-base flex-1">{message}</p>
      <button
        onClick={() => onDismiss(id)}
        aria-label="Dismiss notification"
        className="ml-2 opacity-70 hover:opacity-100 transition-opacity shrink-0"
      >
        ✕
      </button>
    </div>
  )
}